import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlayIcon, MusicalNoteIcon } from "@heroicons/react/24/solid";
import { SparklesIcon } from "@heroicons/react/24/outline";

export default function PlaylistCard({
  id,
  name,
  description,
  image,
  status,
  created_at,
  products = [],
  onPlayClick,
  dark = true,
}) {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);

  const playlistTitle = name || "Curated Playlist";
  const playlistDesc = description || "Exclusive music compilation on BAB Digital.";
  const trackCount = Array.isArray(products) ? products.length : 0;

  const handleCardClick = () => {
    navigate(`/Playlist?id=${id}`, { state: { id, name, description, image } });
  };

  const handlePlayNow = (e) => {
    e.stopPropagation();
    if (onPlayClick) {
      onPlayClick({ id, name, description, image, products });
    } else {
      navigate(`/Playlist?id=${id}&play=true`, {
        state: { id, name, description, image, autoPlay: true },
      });
    }
  };

  // Generate deterministic gradient colors based on playlist ID or name
  const gradientStyles = [
    "from-orange-600/80 via-zinc-900 to-black",
    "from-amber-600/80 via-zinc-900 to-black",
    "from-rose-600/80 via-zinc-900 to-black",
    "from-purple-600/80 via-zinc-900 to-black",
    "from-teal-600/80 via-zinc-900 to-black",
  ];
  const gradientIndex = (id || (name ? name.length : 0)) % gradientStyles.length;
  const chosenGradient = gradientStyles[gradientIndex];

  return (
    <div
      onClick={handleCardClick}
      className={`group relative flex flex-col rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 transform hover:-translate-y-2 border ${
        dark
          ? "bg-zinc-900/70 border-zinc-800/80 hover:border-secondary/60 hover:shadow-2xl hover:shadow-secondary/15"
          : "bg-white border-zinc-200 hover:border-secondary hover:shadow-xl"
      }`}
    >
      {/* Artwork Container */}
      <div className="relative aspect-square w-full overflow-hidden bg-zinc-950 flex items-center justify-center p-3">
        {/* Vinyl Disc Peek-Out behind artwork on hover */}
        <div
          className="absolute right-3 top-3 w-[72%] h-[72%] rounded-full bg-zinc-950 border-2 border-zinc-800/80 shadow-2xl flex items-center justify-center transition-all duration-500 ease-out transform group-hover:translate-x-5 group-hover:-rotate-45 pointer-events-none z-0 opacity-80 group-hover:opacity-100"
          style={{
            backgroundImage:
              "repeating-radial-gradient(circle, #18181b 0px, #18181b 2px, #09090b 3px, #09090b 5px)",
          }}
        >
          <div className="w-8 h-8 rounded-full bg-secondary/80 border-2 border-black flex items-center justify-center">
            <div className="w-2.5 h-2.5 rounded-full bg-black"></div>
          </div>
        </div>

        {/* Cover Art Image or Generative Fallback */}
        <div className="relative z-10 w-full h-full rounded-xl overflow-hidden shadow-lg border border-zinc-700/40">
          {image && !imgError ? (
            <img
              src={image}
              alt={playlistTitle}
              onError={() => setImgError(true)}
              className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
          ) : (
            <div
              className={`w-full h-full bg-gradient-to-br ${chosenGradient} flex flex-col justify-between p-4 relative overflow-hidden`}
            >
              {/* Soundwave background graphic */}
              <div className="absolute inset-0 opacity-15 flex items-center justify-around pointer-events-none">
                <span className="w-1.5 h-16 bg-white rounded-full"></span>
                <span className="w-1.5 h-28 bg-white rounded-full"></span>
                <span className="w-1.5 h-12 bg-white rounded-full"></span>
                <span className="w-1.5 h-24 bg-white rounded-full"></span>
                <span className="w-1.5 h-20 bg-white rounded-full"></span>
              </div>

              <div className="flex items-center justify-between z-10">
                <span className="px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-md text-[10px] font-black uppercase tracking-wider text-secondary border border-secondary/30">
                  BAB Playlist
                </span>
                <SparklesIcon className="w-4 h-4 text-secondary/80 animate-pulse" />
              </div>

              <div className="z-10 flex flex-col items-center justify-center my-auto text-center px-2">
                <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mb-2 shadow-inner">
                  <MusicalNoteIcon className="w-6 h-6 text-secondary" />
                </div>
                <p className="text-white font-black text-sm line-clamp-2 leading-snug drop-shadow-md">
                  {playlistTitle}
                </p>
              </div>

              <div className="flex items-center justify-between text-[11px] text-zinc-300 font-semibold z-10 pt-1 border-t border-white/10">
                <span>SoulFlow</span>
                <span>{trackCount > 0 ? `${trackCount} Tracks` : "Curated Mix"}</span>
              </div>
            </div>
          )}

          {/* Dark Overlay Gradient on Hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity pointer-events-none" />

          {/* Floating Play Button */}
          <button
            onClick={handlePlayNow}
            title="Play Playlist"
            className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-secondary hover:bg-secondary/90 text-white flex items-center justify-center shadow-xl shadow-secondary/40 transform translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 active:scale-95 z-20"
          >
            <PlayIcon className="w-5 h-5 fill-white ml-0.5" />
          </button>
        </div>
      </div>

      {/* Metadata Section */}
      <div className="p-3.5 sm:p-4 flex flex-col flex-1 justify-between">
        <div>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-secondary flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary"></span>
              Curated Playlist
            </span>
            {trackCount > 0 && (
              <span className="text-[10px] font-semibold text-zinc-400 bg-zinc-800/80 px-2 py-0.5 rounded-full">
                {trackCount} tracks
              </span>
            )}
          </div>

          <h4
            className={`font-bold text-sm sm:text-base line-clamp-1 mb-1 transition-colors ${
              dark ? "text-white group-hover:text-secondary" : "text-black group-hover:text-secondary"
            }`}
          >
            {playlistTitle}
          </h4>

          <p
            className={`text-xs line-clamp-2 leading-relaxed ${
              dark ? "text-zinc-400" : "text-gray-600"
            }`}
          >
            {playlistDesc}
          </p>
        </div>

        <div className={`mt-3 pt-2.5 border-t flex items-center justify-between text-[11px] ${
          dark ? "border-zinc-800/60 text-zinc-500" : "border-zinc-200 text-zinc-600"
        }`}>
          <span>BAB Media Official</span>
          <span className="text-secondary font-semibold group-hover:underline">
            Listen Now →
          </span>
        </div>
      </div>
    </div>
  );
}
